/**
 * ContactModal.js
 * -------------------------------
 * Enhanced with extracted flash message system and closing flag
 * Fixed FlashMessageManager eventManager dependency
 */
import ErrorHandler from '../../utils/ErrorHandler.js';
import { SafeDOM } from '../utils/SafeDOM.js';
import { FormSubmitter } from '../utils/FormSubmitter.js';
import { orgContactPhrase } from '../../utils/orgContact.js';
import { ContactFormValidator } from './ContactModalValidator.js';
import { FlashMessageManager } from './FlashMessageManager.js';
import {
  readModalSettings,
  resolveMode,
  applyLayout,
  buildSource,
  pageKey,
  responseTimeFor,
  errorAnchor,
  trapFocus
} from './RedesignModalLayout.js';
import {
  CONSTANTS,
  DEFAULT_SUBMITTER_OPTIONS,
  DEFAULT_FLASH_OPTIONS,
  ERROR_MESSAGES
} from './ContactModalConstants.js';

const isBrowser = SafeDOM.isBrowser;

// Hidden fields a redesign CTA trigger fills in: element id → field name
const CTA_HIDDEN_FIELDS = {
  'contact-source': 'source',
  'contact-cta-source': 'ctaSource',
  'contact-page': 'page',
  'contact-program': 'program'
};

export default class ContactModal {
  constructor(namespace, options = {}) {
    this.namespace = namespace;
    this.modal = null;
    this.form = null;
    this.closeButtons = null;
    this.submitButton = null;
    this.isSubmitting = false;
    this.submitTimeout = null;
    this.eventsBound = false;
    this.isBrowser = isBrowser;
    this.modalConfigs = {};
    this.currentModalType = null;

    // Redesign modal (data-redesign="1"): CTA modes, settings, focus return
    this.isRedesign = false;
    this.modalSettings = {};
    this.currentMode = null;
    this.currentConfig = null;
    this.returnFocusTo = null;

    // Add closing flag to prevent validation during modal close
    this.isClosing = false;

    // Configuration options
    this.options = {
      showSuccessMessage: true,
      showErrorMessage: true,
      resetFormOnSuccess: true,
      scrollToFormOnError: false,
      successMessageDuration: 5000,
      errorMessageDuration: 8000,
      enableAnalytics: true,
      debug: false,
      ...options
    };

    this.submitterOptions = {
      ...DEFAULT_SUBMITTER_OPTIONS,
      debug: this.options.debug,
      analyticsHandler: this.handleAnalytics.bind(this),
      ...options.submitter
    };

    this.flashOptions = {
      ...DEFAULT_FLASH_OPTIONS,
      duration: this.options.successMessageDuration,
      errorDuration: this.options.errorMessageDuration,
      ...options.flash
    };

    if (this.isBrowser) {
      this.errorHandler = new ErrorHandler({ module: 'ContactModal', maxErrors: 25 });
      SafeDOM.init(this.errorHandler);
      this.eventManager = SafeDOM.createEventManager();
      this.formSubmitter = FormSubmitter.createContactSubmitter(this.submitterOptions);
      this.validator = new ContactFormValidator();

      // Fix: Pass eventManager to FlashMessageManager
      this.flashManager = new FlashMessageManager(this.flashOptions, this.errorHandler, this.eventManager);

      this._deferInit();
    }
  }

  _deferInit() {
    SafeDOM.waitForDOMReady(() => this.init());
  }

  init() {
    if (!this.isBrowser) return;
    try {
      this.modal = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.CONTACT_MODAL);
      this.form = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.CONTACT_FORM);
      if (!this.modal || !this.form) throw new Error('Modal or form not found');

      this.closeButtons = SafeDOM.safeQuerySelectorAll(CONSTANTS.SELECTORS.MODAL_CLOSE);
      this.submitButton = SafeDOM.safeQuerySelector(CONSTANTS.SELECTORS.SUBMIT_BUTTON, this.form);
      this.defaultSubmitLabel = this.submitButton?.textContent.trim() || 'Send Message';
      this.submitLabel = this.defaultSubmitLabel;

      // Initialize validator with form element
      this.validator.initialize(this.form);

      this.loadModalConfigs();

      this.isRedesign = this.modal.dataset?.redesign === '1';
      if (this.isRedesign) this.modalSettings = readModalSettings();

      if (!this.eventsBound) {
        this.bindEvents();
        this.eventsBound = true;
      }

      this.errorHandler?.logSuccess('ContactModal initialized', {
        category: 'initialization',
        action: 'success',
        validationRules: this.validator.getAllRules(),
        modalConfigs: Object.keys(this.modalConfigs)
      });
    } catch (err) {
      this.errorHandler?.handleFatalError('Initialization failed', err, {
        category: 'initialization',
        action: 'failed'
      });
    }
  }

  loadModalConfigs() {
    if (!this.form) return;

    // Prefer the non-executing JSON data block; fall back to the legacy
    // data-modal-configs attribute (kept working for cached/rolling HTML).
    const configsEl = document.getElementById('contact-modal-configs');
    const configsData = (configsEl && configsEl.textContent.trim())
      || this.form.dataset.modalConfigs;
    if (configsData) {
      try {
        this.modalConfigs = JSON.parse(configsData);
        console.log('DEBUG: Loaded modal configurations:', Object.keys(this.modalConfigs));
      } catch (error) {
        console.error('Error parsing modal configs:', error);
        this.modalConfigs = {};
      }
    }
  }

  /**
   * Update modal configuration dynamically based on modal type
   */
  updateModalConfiguration(modalType, triggerData = {}) {
    console.log('DEBUG: Updating modal configuration for type:', modalType);

    // Redesign: a configured CTA mode builds on its page type (spec §5.13).
    // A trigger's data-mode wins; its data-modal-type is then the base config.
    const resolved = this.isRedesign
      ? (resolveMode(this.modalConfigs, this.modalSettings, triggerData.mode, modalType)
        || resolveMode(this.modalConfigs, this.modalSettings, modalType))
      : null;
    const config = resolved ? resolved.config
      : ((this.modalConfigs?.[modalType]) ?? (this.modalConfigs?.default)
        ?? (Object.values(this.modalConfigs || {})[0] || {}));
    console.log('DEBUG: Using config:', config);

    this.currentModalType = modalType;
    this.currentMode = resolved ? resolved.modeName : null;
    this.currentConfig = config;

    // Update validator rules from config
    this.validator.updateFromConfig(config);

    // Update form fields
    this.updateFormFieldsFromConfig(config, triggerData);

    // Update checkboxes (the redesign modal lays out its own sections)
    if (this.isRedesign) {
      applyLayout(this.form, config, this.modalSettings);
    } else {
      this.updateCheckboxOptions(config);
    }

    // Redesign CTA trigger: label, preselects, origin fields
    this.applyCtaTrigger(triggerData);
    if (this.isRedesign) this.applyRedesignMode(resolved, config, triggerData);

    // Set modal type in hidden field. A redesign mode sends its page type,
    // which is what the server and the sales funnel expect in modalType.
    const modalTypeField = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.MODAL_TYPE_FIELD);
    if (modalTypeField) {
      modalTypeField.value = resolved
        ? (resolved.mode.pageType || resolved.basePageType || this.modalSettings.pageType || 'default')
        : modalType;
    }

    // Use the configured formId when present so modal submissions follow
    // contact-button-manager routing instead of page-type aliases.
    const formIdField = SafeDOM.safeGetElement('form-id');
    if (formIdField) {
      formIdField.value = config.formId || (modalType === 'installment' ? 'installment' : 'contact-form');
    }

    this.errorHandler?.logSuccess('Modal configuration updated', {
      category: 'modal-configuration',
      action: 'updated',
      modalType,
      configKeys: Object.keys(config)
    });
  }

  /**
   * Reset modal configuration back to default
   */
  resetModalConfiguration() {
    // Reset to default modal type
    this.currentModalType = 'default';
    this.currentMode = null;
    this.currentConfig = null;

    // Reset modal title to the server-rendered page default
    const modalTitle = SafeDOM.safeQuerySelector('#contact-modal h2', this.modal);
    if (modalTitle) {
      modalTitle.textContent = modalTitle.dataset.defaultTitle || 'Contact Us';
    }

    // Reset formId to the page default (stored in data-default-form-id by the server)
    const formIdField = SafeDOM.safeGetElement('form-id');
    if (formIdField) {
      formIdField.value = formIdField.dataset.defaultFormId || 'contact-form';
    }

    // Reset modalType hidden field
    const modalTypeField = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.MODAL_TYPE_FIELD);
    if (modalTypeField) {
      modalTypeField.value = '';
    }

    // Reset pageSlug — dynamic field created on first waitlist/consultation open;
    // clear it so a subsequent plain "Contact Us" open doesn't inherit a stale slug.
    const pageSlugField = SafeDOM.safeGetElement('page-slug');
    if (pageSlugField) {
      pageSlugField.value = '';
    }

    const buttonIdField = SafeDOM.safeGetElement('contact-button-id');
    if (buttonIdField) {
      buttonIdField.value = '';
    }

    // Clear the CTA origin fields and restore the submit label
    Object.keys(CTA_HIDDEN_FIELDS).forEach((id) => {
      const field = SafeDOM.safeGetElement(id);
      if (field) field.value = '';
    });
    this.setSubmitLabel(this.defaultSubmitLabel);

    // Reset validator to default rules
    const defaultConfig = this.modalConfigs?.default || {};
    this.validator.updateFromConfig(defaultConfig);
    this.syncMessageMaxLength(SafeDOM.safeGetElement('message'));

    // Hide dynamic checkboxes (the redesign modal restores the page layout)
    const dynamicCheckboxContainer = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.DYNAMIC_CHECKBOXES);
    if (this.isRedesign) {
      applyLayout(this.form, defaultConfig, this.modalSettings);
    } else if (dynamicCheckboxContainer) {
      dynamicCheckboxContainer.style.display = 'none';
      dynamicCheckboxContainer.innerHTML = '';
    }

    this.errorHandler?.logSuccess('Modal configuration reset', {
      category: 'modal-configuration',
      action: 'reset'
    });
  }

  updateFormFieldsFromConfig(config, triggerData) {
    // Update modal title dynamically based on trigger data
    const modalTitle = SafeDOM.safeQuerySelector('#contact-modal h2', this.modal);
    if (modalTitle && this.currentModalType === 'installment' && triggerData.eventTitle) {
      modalTitle.textContent = `I am interested in the installment plan for ${triggerData.eventTitle}`;
    } else if (modalTitle && (this.currentModalType === 'consultation' || this.currentModalType === 'groups-consultation') && triggerData.eventTitle) {
      modalTitle.textContent = `Contact Us About ${triggerData.eventTitle}`;
    } else if (modalTitle && triggerData.eventTitle && triggerData.facilitator && triggerData.pageSlug) {
      const modalType = this.currentModalType;
      if (modalType === 'waitlist') {
        modalTitle.textContent = `Join the Waitlist for ${triggerData.facilitator}'s ${triggerData.eventTitle} ${triggerData.pageSlug}`;
      } else {
        modalTitle.textContent = config.formTitle || 'Contact Us';
      }
    } else if (modalTitle && config.formTitle) {
      modalTitle.textContent = config.formTitle;
    }

    // Add/update hidden fields for facilitator and pageSlug
    let facilitatorField = SafeDOM.safeGetElement('facilitator-name');
    if (!facilitatorField && this.form) {
      facilitatorField = document.createElement('input');
      facilitatorField.type = 'hidden';
      facilitatorField.id = 'facilitator-name';
      facilitatorField.name = 'facilitatorName';
      this.form.appendChild(facilitatorField);
    }
    if (facilitatorField && triggerData.facilitator) {
      facilitatorField.value = triggerData.facilitator;
    }

    let pageSlugField = SafeDOM.safeGetElement('page-slug');
    if (!pageSlugField && this.form) {
      pageSlugField = document.createElement('input');
      pageSlugField.type = 'hidden';
      pageSlugField.id = 'page-slug';
      pageSlugField.name = 'pageSlug';
      this.form.appendChild(pageSlugField);
    }
    if (pageSlugField && triggerData.pageSlug) {
      pageSlugField.value = triggerData.pageSlug;
    }

    let buttonIdField = SafeDOM.safeGetElement('contact-button-id');
    if (!buttonIdField && this.form) {
      buttonIdField = document.createElement('input');
      buttonIdField.type = 'hidden';
      buttonIdField.id = 'contact-button-id';
      buttonIdField.name = 'buttonId';
      this.form.appendChild(buttonIdField);
    }
    if (buttonIdField) {
      buttonIdField.value = triggerData.buttonId || '';
    }

    // Update message field
    const messageField = SafeDOM.safeGetElement('message');
    const messageLabel = SafeDOM.safeQuerySelector('label[for="message"]', this.form);

    if (messageField) {
      if (config && typeof config.messagePlaceholder === 'string' && config.messagePlaceholder.trim()) {
        messageField.placeholder = config.messagePlaceholder;
      };

      messageField.required = config.messageRequired || false;
      this.syncMessageMaxLength(messageField);

      // Add context from trigger data (support both eventTitle and workshopTitle)
      const title = triggerData.eventTitle || triggerData.workshopTitle;
      const date = triggerData.eventDate || triggerData.workshopDate;
      if (title && date) {
        const contextMessage = `Interested in: ${title}\nDate: ${date}\n\n`;
        messageField.value = contextMessage;
      }
    }

    if (messageLabel && !this.isRedesign) {
      messageLabel.className = config.messageRequired ? CONSTANTS.CLASSES.REQUIRED : '';
    }

    // Update phone field
    const phoneField = SafeDOM.safeGetElement('phone');
    const phoneLabel = SafeDOM.safeQuerySelector('label[for="phone"]', this.form);

    if (phoneField) {
      phoneField.required = config.phoneRequired || false;
    }

    if (phoneLabel && !this.isRedesign) {
      phoneLabel.className = config.phoneRequired ? CONSTANTS.CLASSES.REQUIRED : '';
    }
  }

  /**
   * Redesign CTA triggers (spec §5.13 modes, §9). A trigger's data-cta label
   * becomes the form title and submit text. data-service and data-reason are
   * preselected only when they match one of the form's options (the visitor
   * can still change them); an unmatched value is ignored. data-source,
   * data-cta-source, data-program and the page path go in hidden fields so
   * staff can see where a submission came from.
   */
  applyCtaTrigger(triggerData = {}) {
    const label = typeof triggerData.cta === 'string' ? triggerData.cta.trim() : '';
    if (!label) return;

    const modalTitle = SafeDOM.safeQuerySelector('#contact-modal h2', this.modal);
    if (modalTitle) modalTitle.textContent = label;
    this.setSubmitLabel(label);

    if (triggerData.service) this.preselectOption('services', triggerData.service);
    if (triggerData.reason) this.preselectOption('reason', triggerData.reason);

    const values = {
      source: triggerData.source,
      ctaSource: triggerData.ctaSource || label,
      page: this.isBrowser ? window.location.pathname : '',
      program: triggerData.program
    };
    Object.entries(CTA_HIDDEN_FIELDS).forEach(([id, name]) => {
      this.ensureHiddenField(id, name).value = String(values[name] || '').slice(0, 200);
    });
  }

  /**
   * Redesign CTA modes (spec §5.13): the mode's own title and submit text win
   * over the trigger label, then its preselects and the source template
   * ({page}, {slug}, {placement}); a trigger's data-source wins over the
   * template. Without a mode, only the page path is recorded.
   */
  applyRedesignMode(resolved, config, triggerData = {}) {
    const path = this.isBrowser ? window.location.pathname : '/';
    this.ensureHiddenField('contact-page', 'page').value = path.slice(0, 200);
    if (!resolved) return;

    const { mode } = resolved;
    const label = typeof triggerData.cta === 'string' ? triggerData.cta.trim() : '';
    const title = mode.formTitle || label || config.formTitle;
    const modalTitle = SafeDOM.safeQuerySelector('#contact-modal h2', this.modal);
    if (modalTitle && title) modalTitle.textContent = title;
    this.setSubmitLabel(mode.submitText || label || this.defaultSubmitLabel);

    const preselect = mode.preselect || {};
    Object.entries(preselect).forEach(([name, value]) => {
      (Array.isArray(value) ? value : [value]).forEach((v) => this.preselectOption(name, v));
    });
    const triggerKeys = { services: 'service', reason: 'reason' };
    (mode.preselectFromTrigger || []).forEach((name) => {
      const value = triggerData[triggerKeys[name] || name];
      if (value) this.preselectOption(name, value);
    });

    const sourceField = this.ensureHiddenField('contact-source', 'source');
    if (!sourceField.value) {
      const page = pageKey(path);
      sourceField.value = buildSource(mode.source, {
        page,
        slug: page,
        placement: triggerData.placement
      }).slice(0, 200);
    }

    const program = this.ensureHiddenField('contact-program', 'program');
    program.disabled = !(config.programRequired === true || program.value);
  }

  setSubmitLabel(label) {
    this.submitLabel = label || this.defaultSubmitLabel || 'Send Message';
    if (this.submitButton && !this.isSubmitting) this.submitButton.textContent = this.submitLabel;
  }

  ensureHiddenField(id, name) {
    let field = SafeDOM.safeGetElement(id);
    if (!field && this.form) {
      field = document.createElement('input');
      field.type = 'hidden';
      field.id = id;
      field.name = name;
      this.form.appendChild(field);
    }
    return field;
  }

  /**
   * Checks the option (checkbox/radio) or selects the <option> named `name`
   * whose value matches, ignoring case. Options inside a hidden group are
   * skipped. Returns false, changing nothing, when no option matches.
   */
  preselectOption(name, value) {
    if (!this.form) return false;
    const wanted = String(value).trim().toLowerCase();
    const matches = (v) => String(v).trim().toLowerCase() === wanted;
    const isShown = (el) => {
      const group = el.closest('.checkbox-group');
      return !group || group.style.display !== 'none';
    };

    const select = this.form.querySelector(`select[name="${name}"]`);
    if (select) {
      const option = Array.from(select.options).find((o) => matches(o.value));
      if (option) select.value = option.value;
      return Boolean(option);
    }

    const input = Array.from(this.form.querySelectorAll(`input[name="${name}"]`))
      .find((el) => isShown(el) && matches(el.value));
    if (input) input.checked = true;
    return Boolean(input);
  }

  updateCheckboxOptions(config) {
    const staticCheckboxContainer = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.STATIC_CHECKBOXES);
    const dynamicCheckboxContainer = SafeDOM.safeGetElement(CONSTANTS.SELECTORS.DYNAMIC_CHECKBOXES);

    // Hide static checkboxes when using dynamic config
    if (staticCheckboxContainer) {
      staticCheckboxContainer.style.display = 'none';
    }

    if (!dynamicCheckboxContainer) return;

    if (config.checkboxOptions && config.checkboxOptions.length > 0) {
      // Built as DOM nodes so option text is never parsed as HTML
      const heading = document.createElement('h3');
      heading.textContent = 'Please select which services you\'re interested in.';
      const nodes = [heading];

      config.checkboxOptions.forEach((option, index) => {
        const label = document.createElement('label');
        label.className = CONSTANTS.CLASSES.CHECKBOX_LABEL;
        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'services';
        input.value = String(option);
        input.id = `dynamic-service-${index}`;
        const text = document.createElement('span');
        text.className = 'checkbox-text';
        text.textContent = String(option);
        label.append(input, text);
        nodes.push(label);
      });

      dynamicCheckboxContainer.replaceChildren(...nodes);
      dynamicCheckboxContainer.style.display = 'block';

      console.log('DEBUG: Updated checkboxes for modal type:', this.currentModalType, 'Options:', config.checkboxOptions);
    } else if (Array.isArray(config.checkboxOptions) && config.checkboxOptions.length === 0) {
      // Explicitly empty array: hide both containers (no checkboxes for this modal type)
      dynamicCheckboxContainer.style.display = 'none';
      if (staticCheckboxContainer) staticCheckboxContainer.style.display = 'none';
    } else {
      dynamicCheckboxContainer.style.display = 'none';
      // Show static checkboxes if no dynamic ones
      if (staticCheckboxContainer) {
        staticCheckboxContainer.style.display = 'block';
      }
    }
  }

  bindEvents() {
    if (!this.isBrowser) return;
    this.removeEventListeners();

    // Close button events - set closing flag before closing
    this.closeButtons.forEach(btn => {
      this.eventManager.add(btn, 'click', () => {
        this.isClosing = true; // Set flag before closing
        this.close();
      });
    });

    // ESC key to close - set closing flag
    this.handleEscKeyBound = (e) => {
      if (e.key === 'Escape' && this.isOpen()) {
        this.isClosing = true; // Set flag before closing
        this.close();
      }
    };
    this.eventManager.add(document, 'keydown', this.handleEscKeyBound);

    // Form submission
    this.handleSubmitBound = e => this.handleSubmit(e);
    this.eventManager.add(this.form, 'submit', this.handleSubmitBound);

    // Field validation events - now checks closing flag
    SafeDOM.safeQuerySelectorAll(CONSTANTS.SELECTORS.FORM_INPUTS, this.form)
      .forEach(field => {
        // A radio or checkbox value says nothing about whether one is chosen
        if (field.type === 'radio' || field.type === 'checkbox') {
          this.eventManager.add(field, 'change', () => this.clearFieldError(field));
          return;
        }
        this.eventManager.add(field, 'blur', () => this.validateField(field));
        this.eventManager.add(field, 'input', () => this.clearFieldError(field));
      });

    if (this.isRedesign) this.bindRedesignEvents();
  }

  /**
   * Redesign modal: selects validate like inputs, Tab stays in the dialog,
   * a click on the backdrop closes it, and "Why is this needed?" toggles
   * its note.
   */
  bindRedesignEvents() {
    this.form.querySelectorAll('select').forEach((field) => {
      this.eventManager.add(field, 'blur', () => this.validateField(field));
      this.eventManager.add(field, 'change', () => this.clearFieldError(field));
    });

    this.eventManager.add(this.modal, 'keydown', (e) => trapFocus(e, this.modal));

    this.eventManager.add(this.modal, 'mousedown', (e) => {
      if (e.target === this.modal) {
        this.isClosing = true;
        this.close();
      }
    });

    this.form.querySelectorAll('.f-info__btn').forEach((btn) => {
      this.eventManager.add(btn, 'click', () => {
        const note = document.getElementById(btn.getAttribute('aria-controls'));
        if (!note) return;
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        note.hidden = expanded;
      });
    });
  }

  removeEventListeners() {
    if (!this.isBrowser) return;
    this.eventManager.cleanup();
  }

  _injectRecaptchaScript(siteKey) {
    if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    document.head.appendChild(script);
  }

  async _getRecaptchaToken() {
    const siteKey = this.form?.dataset?.recaptchaSiteKey;
    if (!siteKey) return null;

    // Wait for the reCAPTCHA script to load if it's still in flight
    if (typeof grecaptcha === 'undefined') {
      const scriptInjected = !!document.querySelector('script[src*="recaptcha/api.js"]');
      if (!scriptInjected) {
        console.warn('[ContactModal] reCAPTCHA script not injected');
        return null;
      }
      try {
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('reCAPTCHA load timeout')), 8000);
          const poll = setInterval(() => {
            if (typeof grecaptcha !== 'undefined') {
              clearInterval(poll);
              clearTimeout(timeout);
              resolve();
            }
          }, 50);
        });
      } catch {
        console.warn('[ContactModal] reCAPTCHA did not load in time');
        return null;
      }
    }

    try {
      return await new Promise((resolve, reject) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(siteKey, { action: 'contact_form' }).then(resolve).catch(reject);
        });
      });
    } catch (err) {
      console.warn('[ContactModal] reCAPTCHA token fetch failed:', err.message);
      return null;
    }
  }

  open() {
    if (!this.isBrowser || !this.modal) return false;
    try {
      if (this.isRedesign) {
        this.returnFocusTo = document.activeElement;
        // A plain open (no CTA mode) still needs the page's layout and path
        if (!this.currentConfig) {
          this.currentConfig = this.modalConfigs?.default || {};
          applyLayout(this.form, this.currentConfig, this.modalSettings);
          this.applyRedesignMode(null, this.currentConfig);
        }
      }

      SafeDOM.safeAddClass(this.modal, CONSTANTS.CLASSES.MODAL_OPEN);
      SafeDOM.safeSetStyle(this.modal, { display: this.isRedesign ? 'flex' : 'block' });
      SafeDOM.safeAddClass(document.body, CONSTANTS.CLASSES.MODAL_OPEN);

      this.trackUmami('consultation_form_open');

      // Lazy-load reCAPTCHA script on first modal open
      const siteKey = this.form?.dataset?.recaptchaSiteKey;
      if (siteKey) this._injectRecaptchaScript(siteKey);

      // Warn upfront if the browser won't keep cookies at all — submission
      // relies on a cookie-based CSRF token, so this is worth surfacing
      // before the visitor spends time filling the form out, not just after
      // a failed submit.
      if (!this._checkCookieSupport()) {
        this.showFlashWarning(
          `Your browser appears to be blocking cookies for this site, which may prevent this form from submitting. If you’re in Private/Incognito mode, try a regular window — or reach us directly at ${orgContactPhrase()}.`,
          0
        );
      }

      const firstInput = this.isRedesign
        ? SafeDOM.safeGetElement('firstName')
        : SafeDOM.safeQuerySelector(CONSTANTS.SELECTORS.FIRST_INPUT, this.form);
      if (firstInput) {
        SafeDOM.safeFocus(firstInput, { delay: CONSTANTS.TIMING.FOCUS_DELAY });
      }

      this.errorHandler?.logSuccess('Modal opened', {
        category: 'modal-operation',
        action: 'open',
        modalType: this.currentModalType
      });
      return true;
    } catch (err) {
      this.errorHandler?.handleError('Failed to open modal', err, {
        category: 'modal-operation',
        action: 'open-failed'
      });
      return false;
    }
  }

  /**
   * Best-effort check for whether the browser will accept first-party
   * cookies at all. CSRF validation is cookie-based, so a browser refusing
   * cookies entirely (Private/Incognito mode, a strict privacy extension)
   * will fail submission no matter how many times the token gets refreshed.
   *
   * This writes a plain, JS-readable test cookie — it can't test the actual
   * httpOnly CSRF/session cookies directly (JS can't read those by design),
   * but the two are blocked together in practice under the same privacy
   * posture, so this is a reasonable proxy. Treat the result as a heuristic,
   * not a guarantee — word any resulting message accordingly ("may have
   * trouble", not "will fail").
   *
   * @returns {boolean}
   */
  _checkCookieSupport() {
    if (!this.isBrowser) return true;
    try {
      const testName = 'irh_cookie_test';
      document.cookie = `${testName}=1; path=/; max-age=10; SameSite=Lax`;
      const supported = document.cookie.indexOf(`${testName}=1`) !== -1;
      document.cookie = `${testName}=; path=/; max-age=0`;
      return supported;
    } catch {
      return false;
    }
  }

  close() {
    if (!this.isBrowser || !this.modal) return false;
    try {
      // Set closing flag to prevent validation during close
      this.isClosing = true;

      SafeDOM.safeRemoveClass(this.modal, CONSTANTS.CLASSES.MODAL_OPEN);
      SafeDOM.safeSetStyle(this.modal, { display: 'none' });
      SafeDOM.safeRemoveClass(document.body, CONSTANTS.CLASSES.MODAL_OPEN);

      this.clearAllFieldErrors();
      this.resetModalConfiguration();
      this.resetForm(); // Reset form when closing

      // Redesign: focus goes back to the button that opened the modal
      if (this.isRedesign && this.returnFocusTo && typeof this.returnFocusTo.focus === 'function'
          && document.contains(this.returnFocusTo)) {
        this.returnFocusTo.focus();
      }
      this.returnFocusTo = null;

      this.errorHandler?.logSuccess('Modal closed', {
        category: 'modal-operation',
        action: 'close',
        modalType: this.currentModalType
      });

      // Reset closing flag after a short delay to ensure all blur events are processed
      setTimeout(() => {
        this.isClosing = false;
      }, 100);

      return true;
    } catch (err) {
      // Make sure to reset the flag even if there's an error
      this.isClosing = false;

      this.errorHandler?.handleError('Failed to close modal', err, {
        category: 'modal-operation',
        action: 'close-failed'
      });
      return false;
    }
  }

  isOpen() {
    if (!this.isBrowser || !this.modal) return false;
    return this.modal.style.display === 'block' || this.modal.classList.contains(CONSTANTS.CLASSES.MODAL_OPEN);
  }

  // Update validateField method to check the closing flag
  validateField(field) {
    // Skip validation if modal is in the process of closing
    if (this.isClosing) {
      return true;
    }

    if (!field) return false;

    const value = field.value?.trim();
    const name = field.name;

    // Use the extracted validator
    const validation = this.validator.validateField(name, value);

    if (!validation.valid) {
      this.showFieldError(field, validation.error.message);
      return false;
    } else if (field.dataset.serverErrorValue === field.value) {
      // The server rejected this exact value; passing the client rules
      // doesn't clear its message until the visitor changes the field.
      return false;
    } else {
      this.clearFieldError(field);
      return true;
    }
  }

  async handleSubmit(e) {
    e.preventDefault();

    if (!this.isBrowser || this.isSubmitting) return;

    try {
      this.isSubmitting = true;
      this.setLoading(true);
      this.clearAllFieldErrors();

      // Use the extracted validator - form validation happens here !!!!
      const validation = this.validator.validateForm(this.form);
      if (!validation.valid) {
        // Show error and RETURN - this stops submission
        this.showError(validation.message);
        validation.errors.forEach(error => {
          const fieldElement = SafeDOM.safeQuerySelector(
            `input[name="${error.field}"], textarea[name="${error.field}"], select[name="${error.field}"]`,
            this.form
          );
          if (fieldElement) {
            this.showFieldError(fieldElement, error.message);
          }
        });
        return;
      }

      // Create FormData to inspect it
      const inspectData = new FormData(this.form);
      for (let [key, value] of inspectData.entries()) {
      }

      // Fetch reCAPTCHA v3 token if the form has a site key configured
      const recaptchaToken = await this._getRecaptchaToken();

      const result = await this.formSubmitter.submit(this.form, {
        additionalData: {
          // additionalData overwrites form fields, so keep a CTA's source
          source: SafeDOM.safeGetElement('contact-source')?.value || CONSTANTS.FORM_SUBMISSION.SOURCE,
          type: CONSTANTS.FORM_SUBMISSION.TYPE,
          modalInstance: this.constructor.name,
          modalType: this.currentModalType,
          timestamp: new Date().toISOString(),
          validationRules: this.validator.getAllRules(),
          // Re-checked at submit time (not just cached from modal-open) so a
          // failed attempt's log entry reflects cookie support at the moment
          // it actually mattered. Server reads this as `cookiesSupported` on
          // the request body — see contactSubmissionService.js / csrf.js.
          cookiesSupported: this._checkCookieSupport(),
          ...(recaptchaToken && { recaptchaToken })
        }
      });

      console.log('DEBUG: Contact form submission result:', result);
      this.handleSubmissionSuccess(result);

      this.errorHandler?.logSuccess('Form submitted successfully', {
        category: 'form-submission',
        action: 'success',
        submissionId: result.submissionId,
        modalType: this.currentModalType
      });

    } catch (err) {
      console.error('Form submission error:', err);

      this.errorHandler?.handleFatalError('Form submission failed', err, {
        category: 'form-submission',
        action: 'failed',
        errorCategory: err.category,
        modalType: this.currentModalType
      });

      this.handleSubmissionError(err);

    } finally {
      this.isSubmitting = false;
      this.setLoading(false);
    }
  }

  handleSubmissionSuccess(result) {
    this.trackSubmissionEvent('success', { submissionId: result.submissionId });
    this.trackUmami('consultation_form_submit');

    // Read before close() resets the mode
    const successMessage = this.isRedesign ? this.redesignSuccessMessage() : null;

    // Close modal first
    this.close();

    // Show success flash message
    if (this.flashManager) {
      this.flashManager.showSuccess(
        successMessage || 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.'
      );
    }

    this.errorHandler?.logSuccess('Form submitted successfully with flash message', {
      category: 'form-submission',
      action: 'success',
      submissionId: result.submissionId,
      modalType: this.currentModalType
    });
  }

  /**
   * Spec §5.13: "Your request was sent. A member of our team will contact you
   * within {response time}." A mode's successMessage replaces it.
   */
  redesignSuccessMessage() {
    const config = this.currentConfig || this.modalConfigs?.default || {};
    const responseTime = responseTimeFor(config, this.modalSettings);
    if (config.successMessage) {
      return config.successMessage.replace(/\{responseTime\}/g, responseTime || 'a few business days');
    }
    return responseTime
      ? `Your request was sent. A member of our team will contact you within ${responseTime}.`
      : 'Your request was sent. A member of our team will contact you soon.';
  }

  /**
   * Server validation errors ({ validationErrors: [{ field, message }] }) from
   * a 400 response, or [] when the response has none.
   */
  serverValidationErrors(error) {
    try {
      const body = JSON.parse(error?.originalError?.responseText || error?.responseText || '{}');
      return Array.isArray(body.validationErrors) ? body.validationErrors : [];
    } catch {
      return [];
    }
  }

  handleSubmissionError(error) {
    this.trackSubmissionEvent('error', {
      errorCategory: error.category,
      errorMessage: error.message
    });

    // Redesign: the modal stays open with the visitor's input. Errors the
    // visitor can fix go next to their fields; the rest go in a flash.
    if (this.isRedesign) {
      const fieldErrors = this.serverValidationErrors(error)
        .filter((e) => e && e.field && this.form?.querySelector(`[name="${e.field}"]:not(:disabled)`));
      if (fieldErrors.length) {
        fieldErrors.forEach((e) => {
          this.showFieldError(e.field, e.message || 'Please check this field');
          const el = this.form.querySelector(`[name="${e.field}"]:not(:disabled)`);
          if (el) el.dataset.serverErrorValue = el.value;
        });
        const first = this.form.querySelector(`[name="${fieldErrors[0].field}"]:not(:disabled)`);
        if (first) first.focus();
        if (this.flashManager) this.flashManager.showError('Please correct the highlighted fields.');
        return;
      }
    } else {
      // Close modal first
      this.close();
    }

    // CSRF messaging is dynamic — FormSubmitter.categorizeError() already
    // distinguishes "stale token, never refreshed" from "refreshed and it
    // still failed" (the latter needs a different message + a persistent
    // display; see error.csrfRefreshAttempted). Prefer that computed
    // message over the static per-category lookup for this one category
    // rather than re-deriving a coarser version of the same thing here.
    const userMessage = error.category === 'csrf' && error.userMessage
      ? error.userMessage
      : (ERROR_MESSAGES[error.category] || ERROR_MESSAGES.default);

    // A refresh-already-failed CSRF error is a genuine dead end the visitor
    // needs time to read (it points them to call/email instead) — don't
    // let it auto-dismiss on the same timer as an ordinary error.
    const duration = error.category === 'csrf' && error.csrfRefreshAttempted ? 0 : null;

    // Show error flash message
    if (this.flashManager) {
      this.flashManager.showError(userMessage, duration);
    }
  }

  handleAnalytics(eventData) {
    if (this.namespace?.trackClick) {
      this.namespace.trackClick(null, eventData);
    }

    if (typeof this.submitterOptions.customAnalytics === 'function') {
      this.submitterOptions.customAnalytics(eventData);
    }
  }

  /**
   * Umami event for the modal (spec §10), with the trigger's source and the
   * form mode as properties. Never sends field values. Umami only loads in
   * production for non-staff visitors, so this is a no-op elsewhere.
   */
  trackUmami(name) {
    if (!this.isBrowser) return;
    try {
      const source = SafeDOM.safeGetElement('contact-source')?.value || '';
      window.umami?.track(name, { source, mode: this.currentMode || this.currentModalType || 'default' });
    } catch { /* analytics is optional */ }
  }

  trackSubmissionEvent(event, data = {}) {
    this.handleAnalytics({
      event: `contact_form_${event}`,
      component: 'ContactModal',
      modalType: this.currentModalType,
      ...data
    });
  }

  showFieldError(field, msg) {
    if (!this.isBrowser || !field) return;

    if (typeof field === 'string') {
      field = SafeDOM.safeQuerySelector(`input[name="${field}"], textarea[name="${field}"], select[name="${field}"]`, this.form);
      if (!field) return;
    }

    this.clearFieldError(field);

    const errorEl = SafeDOM.safeCreateElement(this.isRedesign ? 'span' : 'div', {
      className: this.isRedesign ? `${CONSTANTS.CLASSES.FIELD_ERROR} f-err` : CONSTANTS.CLASSES.FIELD_ERROR,
      textContent: msg
    });

    if (errorEl) {
      SafeDOM.safeAddClass(field, 'field-error-state');
      if (this.isRedesign) {
        errorEl.id = `contact-err-${field.name}`;
        errorEl.setAttribute('role', 'alert');
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', errorEl.id);
        SafeDOM.safeAppendChild(errorAnchor(field), errorEl);
      } else {
        SafeDOM.safeAppendChild(field.parentNode, errorEl);
      }
    }
  }

  clearFieldError(field) {
    if (!this.isBrowser || !field) return;

    SafeDOM.safeRemoveClass(field, 'field-error-state');
    delete field.dataset.serverErrorValue;
    const anchor = this.isRedesign ? errorAnchor(field) : field.parentNode;
    const errorEl = SafeDOM.safeQuerySelector(`.${CONSTANTS.CLASSES.FIELD_ERROR}`, anchor);
    if (errorEl) {
      SafeDOM.safeRemoveElement(errorEl);
    }
    if (this.isRedesign) {
      this.form?.querySelectorAll(`[name="${field.name}"]`).forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
    }
  }

  clearAllFieldErrors() {
    if (!this.isBrowser || !this.form) return;

    SafeDOM.safeQuerySelectorAll(`.${CONSTANTS.CLASSES.FIELD_ERROR}`, this.form).forEach(el => {
      SafeDOM.safeRemoveElement(el);
    });

    SafeDOM.safeQuerySelectorAll(CONSTANTS.SELECTORS.FORM_INPUTS, this.form).forEach(field => {
      SafeDOM.safeRemoveClass(field, 'field-error-state');
    });
    this.form.querySelectorAll('[data-server-error-value]').forEach((el) => {
      delete el.dataset.serverErrorValue;
    });
    if (this.isRedesign) {
      this.form.querySelectorAll('[aria-invalid]').forEach((el) => {
        el.removeAttribute('aria-invalid');
        el.removeAttribute('aria-describedby');
      });
    }
  }

  setLoading(loading) {
    if (!this.isBrowser || !this.submitButton) return;

    if (loading) {
      this.submitButton.disabled = true;
      this.submitButton.textContent = 'Sending...';
      SafeDOM.safeAddClass(this.submitButton, 'loading-state');
    } else {
      this.submitButton.disabled = false;
      this.submitButton.textContent = this.submitLabel || 'Send Message';
      SafeDOM.safeRemoveClass(this.submitButton, 'loading-state');
    }
  }

  showMessage(msg, type) {
    if (!this.isBrowser || !this.form) return;

    const existingMsg = SafeDOM.safeQuerySelector(CONSTANTS.SELECTORS.FORM_MESSAGE, this.form);
    if (existingMsg) {
      SafeDOM.safeRemoveElement(existingMsg);
    }

    const messageEl = SafeDOM.safeCreateElement('div', {
      className: `form-message ${type}`,
      textContent: msg
    });

    if (messageEl) {
      const firstChild = this.form.firstChild;
      if (firstChild) {
        this.form.insertBefore(messageEl, firstChild);
      } else {
        SafeDOM.safeAppendChild(this.form, messageEl);
      }

      if (type === 'error') {
        setTimeout(() => SafeDOM.safeRemoveElement(messageEl), CONSTANTS.TIMING.ERROR_MESSAGE_TIMEOUT);
      }
    }
  }

  showSuccess(msg) {
    this.showMessage(msg, 'success');
  }

  showError(msg) {
    this.showMessage(msg, 'error');
  }

  resetForm() {
    if (!this.isBrowser || !this.form) return;

    this.form.reset();
    this.clearAllFieldErrors();

    const message = SafeDOM.safeQuerySelector(CONSTANTS.SELECTORS.FORM_MESSAGE, this.form);
    if (message) {
      SafeDOM.safeRemoveElement(message);
    }
  }

  // Optional: Add utility methods for debugging
  setClosingState(isClosing = true) {
    this.isClosing = isClosing;

    // Auto-reset after a delay if setting to true
    if (isClosing) {
      setTimeout(() => {
        this.isClosing = false;
      }, 150);
    }
  }

  isModalClosing() {
    return this.isClosing;
  }

  // Configuration and utility methods
  updateSubmitterOptions(options) {
    Object.assign(this.submitterOptions, options);
    if (this.formSubmitter) {
      this.formSubmitter = FormSubmitter.createContactSubmitter(this.submitterOptions);
    }
  }

  updateFlashOptions(options) {
    Object.assign(this.flashOptions, options);
    if (this.flashManager) {
      this.flashManager.updateOptions(options);
    }
  }

  setEndpoints(endpoints) {
    this.updateSubmitterOptions({ endpoints });
  }

  setDebugMode(enabled) {
    this.updateSubmitterOptions({ debug: enabled });
  }

  setFlashPosition(position) {
    this.updateFlashOptions({ position });
  }

  setFlashDuration(duration, errorDuration = null) {
    this.updateFlashOptions({
      duration,
      errorDuration: errorDuration || duration * 1.6
    });
  }

  setMessageMinLength(minLength) {
    this.validator.updateFieldRules('message', { minLength: parseInt(minLength) || 0 });
  }

  // The textarea's maxlength comes from the page's default form; match it to the
  // form that opened the modal so the browser and the character counter agree
  syncMessageMaxLength(messageField) {
    const max = this.validator.getFieldRules('message').maxLength;
    if (messageField && max > 0) messageField.maxLength = max;
  }

  setMessageRequired(required) {
    this.validator.updateFieldRules('message', { required: Boolean(required) });
  }

  setPhoneRequired(required) {
    this.validator.updateFieldRules('phone', { required: Boolean(required) });
  }

  // Flash message convenience methods
  showFlashSuccess(message, duration = null) {
    return this.flashManager?.showSuccess(message, duration);
  }

  showFlashError(message, duration = null) {
    return this.flashManager?.showError(message, duration);
  }

  showFlashWarning(message, duration = null) {
    return this.flashManager?.showWarning(message, duration);
  }

  showFlashInfo(message, duration = null) {
    return this.flashManager?.showInfo(message, duration);
  }

  clearAllFlashMessages() {
    this.flashManager?.clearAll();
  }

  // Getter methods
  getValidationRules() {
    return this.validator.getAllRules();
  }

  getCurrentModalType() {
    return this.currentModalType;
  }

  getModalConfigs() {
    return { ...this.modalConfigs };
  }

  getFlashStats() {
    return this.flashManager?.getStats() || { activeCount: 0 };
  }

  getRecentErrors(min = 5) {
    return this.errorHandler?.getRecentErrors(min) || [];
  }

  hasCriticalIssues() {
    return this.errorHandler?.hasFatalErrors() || false;
  }

  clearErrorHistory() {
    this.errorHandler?.clearErrors();
  }

  isInitialized() {
    return this.isBrowser && !!(this.modal && this.form);
  }

  destroy() {
    this.removeEventListeners();
    this.eventsBound = false;
    if (this.submitTimeout) clearTimeout(this.submitTimeout);

    this.flashManager?.destroy();

    this.errorHandler?.logSuccess('ContactModal destroyed', {
      category: 'lifecycle',
      action: 'destroy'
    });
  }

  static create(namespace, options = {}) {
    if (!isBrowser) return null;
    return new ContactModal(namespace, options);
  }
}
